<template>
  <div class="ta-page-bidder-registrations">
    <PageHeader :title="$t('Bidder Registrations')"></PageHeader>
    <div class="d-flex justify-content-start">
      <div class="ta-bidder-registration-list">
        <div class="ta-bidder-registration-list-header">
          <div class="ta-per-page-wrapper">
            {{ $t("Show") }}
            <b-form-select
              class="mx-2"
              v-model="limit"
              @change="updatePagination"
              :options="perPageOptions"
            ></b-form-select>
            {{ $t("entries") }}
          </div>
        </div>
        <div v-if="bidderRegistrations.loading" class="ta-bidder-registration-loader">
          <b-spinner label="Spinning"></b-spinner>
          <span class="mt-2">{{ $t("Loading...") }}</span>
        </div>
        <template v-if="!bidderRegistrations.loading">
          <div class="ta-bidder-registration-list-wrapper">
            <table class="table table-sm">
              <thead>
              <tr>
                <th scope="col">
                  {{ $t("Sale No") }}
                </th>
                <th scope="col">
                  {{ $t("Sale Title") }}
                </th>
                <th scope="col">
                  {{ $t("Registration Date") }}
                </th>
                <th scope="col">
                  {{ $t("Status") }}
                </th>
                <th scope="col">
                  {{ $t("Actions") }}
                </th>
              </tr>
              </thead>
              <tbody v-if="bidderRegistrations.results.length">
              <tr v-for="registration in bidderRegistrations.results" :key="registration.uuid">
                <td>
                  {{ registration.auction ? registration.auction.sale_no : '' }}
                </td>
                <td>
                  {{ registration.auction ? ($options.filters.stripTags($options.filters.currentLocale(registration.auction.title))) : '' }}
                </td>
                <td>
                  {{ registration.created_at | datetime }}
                </td>
                <td>
                  <span
                    class="badge ta-uc-first"
                    :class="getStatusBadgeClass(registration.status)"
                  >{{ registration.status }}</span>
                </td>
                <td>
                  <router-link
                    v-if="registration.auction"
                    :to="{ name: 'AuctionViewTimed', params: { uuid: registration.auction.uuid } }"
                    class="btn btn-sm btn-primary btn-view"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" style="width: 16px; display: inline-block" fill="none"
                         viewBox="0 0 24 24"
                         stroke="currentColor" stroke-width="2">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                      <path stroke-linecap="round" stroke-linejoin="round"
                            d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                    </svg>
                    {{ $t("View") }}
                  </router-link>
                </td>
              </tr>
              </tbody>
              <tbody v-else>
              <tr>
                <td colspan="5">
                  <p class="text-muted text-center py-3">
                    {{ $t(`You don't have data`) }}
                  </p>
                </td>
              </tr>
              </tbody>
            </table>
          </div>
          <div class="ta-bidder-registration-list-footer" v-if="bidderRegistrations.totalRows > 0">
            <div class="ta-bidder-registration-pagination-summary">
              {{
                $t("Showing {start} to {finish} of {total} entries", {
                  start:
                    (bidderRegistrations.pagination.page - 1) * bidderRegistrations.pagination.limit +
                    1,
                  finish:
                    bidderRegistrations.pagination.page * bidderRegistrations.pagination.limit >
                    bidderRegistrations.totalRows
                      ? bidderRegistrations.totalRows
                      : bidderRegistrations.pagination.page * bidderRegistrations.pagination.limit,
                  total: bidderRegistrations.totalRows,
                })
              }}
            </div>
            <b-pagination
              v-model="page"
              :total-rows="bidderRegistrations.totalRows"
              :per-page="bidderRegistrations.pagination.limit"
              @input="updatePagination"
              class="ta-bidder-registration-list-pagination"
            ></b-pagination>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import {mapState, mapGetters, mapActions} from "vuex";
import {
  BIDDER_REGISTRATION_PAGINATION_PAGE,
  BIDDER_REGISTRATION_PAGINATION_LIMIT,
} from "../constants";
import logger from "../logger";
import PageHeader from "@/components/PageHeader";

export default {
  name: "BidderRegistrations",
  components: {PageHeader},
  data: () => {
    return {
      limit: BIDDER_REGISTRATION_PAGINATION_LIMIT,
      page: BIDDER_REGISTRATION_PAGINATION_PAGE,
      filters: {},
      sort: 'registration_date',
      sortBy: 'desc',
    };
  },

  computed: {
    ...mapState(["perPageOptions", "bidderRegistrations"]),
    ...mapGetters(["currentLocale"]),
  },
  watch: {
    "$route.query": {
      immediate: true,
      handler(newVal) {
        if (newVal.page) this.page = parseInt(newVal.page);
        if (newVal.limit) this.limit = parseInt(newVal.limit);

        this.getUpdatedBidderRegistrations();
      },
    },
  },

  methods: {
    ...mapActions(["getBidderRegistrations"]),

    updatePagination() {
      this.updateRoute();
    },

    updateRoute() {
      this.$router
        .push({
          path: this.$route.path,
          query: {
            ...this.$route.query,
            page: this.page,
            limit: this.limit,
          },
        })
        .catch((err) => {
          logger.error("Failed to update route for bidder registrations", err);
        });
    },

    getUpdatedBidderRegistrations() {
      this.getBidderRegistrations({
        pagination: {page: this.page, limit: this.limit},
        filters: this.filters,
        sort: this.sort,
        sortBy: this.sortBy,
      });
    },

    getStatusBadgeClass(status) {
      switch (status) {
        case 'approved':
          return 'badge-success';
        case 'pending':
          return 'badge-warning';
        case 'rejected':
          return 'badge-danger';
        default:
          return 'badge-secondary';
      }
    }
  },
};
</script>

<style scoped lang="scss">
.ta-bidder-registration-list-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 1rem;
}

.ta-bidder-registration-loader {
  height: 300px;
  width: 100%;
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
}

.ta-per-page-wrapper {
  display: flex;
  align-items: center;
}

.ta-bidder-registration-list {
  flex: 1;
}

.ta-bidder-registration-list-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.ta-uc-first {
  text-transform: capitalize;
}

.pagination.ta-bidder-registration-list-pagination {
  margin-bottom: 0 !important;
}
</style>

